import _cloneDeep from 'lodash/cloneDeep';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _mergeWith from 'lodash/mergeWith';
import _pickBy from 'lodash/pickBy';

import { fixDateWithTimezone, fixDateWithTimezoneParam } from 'components/MagicTimezonePicker';
import { applyWinProbability, deriveWinProbability } from 'containers/Campaign/Games/winOdds';
import { getRaffleGrandPrizePositionsCount } from 'containers/Campaign/Raffle/utils';
import { TCampaignCreatePage } from 'containers/Campaign/i18n/TModules';
import { customMergeForImages, getStatic } from 'containers/Campaign/utils';
import { defaultValuesForDiff, getDiff, getFilteredDiff } from 'containers/utils';
import { doesFilterHaveValue } from 'utils/audience';
import i18n from 'utils/i18n';

/**
 * Recursively removes image_id from image objects that have image_url.
 * This ensures predefined CDN images don't carry over image_id from previous selections.
 */
const cleanImageIds = (obj: any): any => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => cleanImageIds(item));
  }

  // Check if this is an image object with both image_url and image_id
  if (
    obj.type === 'image' &&
    obj.value &&
    typeof obj.value === 'object' &&
    obj.value.image_url &&
    obj.value.image_id
  ) {
    const { image_id, ...cleanedValue } = obj.value;
    return { ...obj, value: cleanedValue };
  }

  // Recursively clean nested objects
  const cleanedObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cleanedObj[key] = cleanImageIds(obj[key]);
    }
  }
  return cleanedObj;
};

export const getRequestConfig = ({
  toUpdate,
  baseCampaign,
  initialFormValues,
  makerCheckerCampaign,
  campaign_request_id,
  isRoleTenantAdmin,
  isImageFeatureEnabled = false
}) => {
  const currentFormData = prepareCampaignData(toUpdate, isImageFeatureEnabled);
  const sanitizedBaseCampaign = prepareCampaignData(baseCampaign);
  const sanitizedInitialFormValues = prepareCampaignData(initialFormValues);

  const currentChangedValues = getDiff(sanitizedInitialFormValues, currentFormData);
  const currentChangedKeys = Object.keys(currentChangedValues);
  const previousChangedKeys = _has(currentFormData, 'changedProperties')
    ? (Array.from(currentFormData.changedProperties) as string[])
    : [];

  const validKeys = new Set([...currentChangedKeys, ...previousChangedKeys]);
  const filteredDiffData: any = getFilteredDiff(
    sanitizedBaseCampaign,
    currentFormData,
    validKeys,
    defaultValuesForDiff
  );

  // Ensure 'auto_enrol_users' and 'enrolment_type' are handled correctly
  if (
    _has(currentFormData, 'auto_enrol_users') &&
    _has(sanitizedInitialFormValues, 'auto_enrol_users') &&
    currentFormData.auto_enrol_users !== sanitizedInitialFormValues.auto_enrol_users
  ) {
    filteredDiffData.enrolment_type = currentFormData.enrolment_type;
  }

  if (_has(filteredDiffData, 'id')) {
    delete filteredDiffData.id;
  }

  if (_has(filteredDiffData, 'changedProperties')) {
    delete filteredDiffData.changedProperties;
  }

  const raffleGrandPrizesCount = getRaffleGrandPrizePositionsCount(
    currentFormData?.raffle_grand_prizes || []
  );
  const shouldClearRafflePrizeLimit =
    currentFormData?.campaign_type === 'raffle' &&
    raffleGrandPrizesCount < 2 &&
    sanitizedBaseCampaign?.prizes_per_raffle_winner === 1 &&
    currentFormData?.prizes_per_raffle_winner !== 1;

  if (shouldClearRafflePrizeLimit) {
    filteredDiffData.prizes_per_raffle_winner = null;
    currentFormData.prizes_per_raffle_winner = null;
  }

  let requestConfig = {} as {
    method: string;
    endpoint: string;
    formdata: Record<string, any>;
    successMessage: string;
  };

  switch (true) {
    /* Update new changes for existing campaign request */
    case makerCheckerCampaign && !_isEmpty(campaign_request_id) && isRoleTenantAdmin === false:
      requestConfig = {
        method: 'put',
        endpoint: `v4/dash/campaign_requests/${campaign_request_id}`,
        formdata: {
          properties: filteredDiffData
        },
        successMessage: i18n.t(`${TCampaignCreatePage}.CampaignRequestUpdated`)
      };
      break;
    /* Apply changes to the campaign for editing active campaign */
    case makerCheckerCampaign &&
      !campaign_request_id &&
      baseCampaign.state !== 'draft' &&
      isRoleTenantAdmin === false:
      requestConfig = {
        method: 'patch',
        endpoint: `/v4/dash/campaigns/${toUpdate.id}/apply_changes`,
        formdata: {
          properties: filteredDiffData
        },
        successMessage: i18n.t(`${TCampaignCreatePage}.CampaignIsSubmittedForApproval`)
      };
      break;
    default:
      requestConfig = {
        method: 'put',
        endpoint: `/v4/dash/campaigns/${currentFormData.id}`,
        formdata: currentFormData,
        successMessage: i18n.t(`${TCampaignCreatePage}.CampaignUpdated`)
      };
      break;
  }
  return requestConfig;
};
export const prepareCampaignData = (data: any, isImageFeatureEnabled = false) => {
  const sanitizedData = getCampaignPreparedToBeSent(data);
  fixDateWithTimezoneParam(sanitizedData, 'start_date', 'start_time');
  fixDateWithTimezoneParam(sanitizedData, 'end_date', 'end_time');

  // Convert expiry_fixed_date in outcomes using campaign timezone
  const timezoneSource = sanitizedData.start_time || sanitizedData.end_time;
  if (timezoneSource && Array.isArray(sanitizedData.outcomes)) {
    const tzMatch = timezoneSource.match(/([+-][0-9]{2}:[0-9]{2})$/);
    const tzSuffix = tzMatch ? tzMatch[1] : '';
    const convertExpiryDate = (item: any) => {
      if (item?.expiry_fixed_date && !item.expiry_fixed_date.endsWith('Z')) {
        const dateStr = item.expiry_fixed_date.substring(0, 10);
        const timeStr = item.expiry_fixed_date.substring(11, 16);
        item.expiry_fixed_date = fixDateWithTimezone(dateStr, timeStr + tzSuffix);
      }
      if (Array.isArray(item?.outcomes)) {
        item.outcomes.forEach(convertExpiryDate);
      }
    };
    sanitizedData.outcomes.forEach(convertExpiryDate);
  }

  return sanitizeCampaign(sanitizedData, isImageFeatureEnabled);
};

const transformBubblePopData = (data: any) => {
  const { outcomes, ...rest } = data;

  const outcomePerScore = (outcomes || []).map(outcome => ({
    score: outcome.score,
    outcomes: [outcome]
  }));

  // bubble_configuration now lives in display_properties, not game_settings
  const { bubble_configuration, bubble_spawn_rate, ...cleanGameSettings } =
    rest.game_settings || {};

  const updatedGameSettings = {
    ...cleanGameSettings,
    outcome_per_score: outcomePerScore
  };

  return {
    ...rest,
    game_settings: updatedGameSettings
  };
};

export const getCampaignPreparedToBeSent = formValues => {
  let data = _cloneDeep(formValues);

  // Keep the legacy duration field aligned with the editable end_date field.
  // Duplicated campaigns can carry a stale duration_ends_at value even after
  // the user switches to "No End Date".
  data.duration_ends_at = data.end_date ?? null;

  // Remove duplicate-flow and UI-only fields before create/update requests.
  delete data.duplicateID;
  delete data.end_date_backup;
  delete data.hideDurationEndDate;
  delete data.hideDurationEndDateInit;

  // Necessary step on the create flow.
  // Quiz and Survey are the types of games on the server side.
  // Shouldn't affect the update flow.
  if (data.campaign_type === 'quiz' || data.campaign_type === 'survey') {
    data.game_type = data.campaign_type;
    data.campaign_type = 'game';
  }

  if (data.campaign_type === 'streak') {
    if (data.streak_campaign_mechanics?.streak_mode === 'progressive') {
      if (data.streak_campaign_mechanics?.streak_type === 'cumulative_spend') {
        data.streak_intervals =
          data.milestones?.map((milestone, index) => ({
            id: milestone?.id,
            position: index + 1,
            target_spend: Number(milestone.target_spend),
            streak_period: data.temp.streak_period,
            streak_unit: Number(milestone.streak_unit),
            outcomes: milestone.outcomes
          })) || [];
      } else {
        data.streak_intervals =
          data.milestones?.map((milestone, index) => ({
            id: milestone?.id,
            position: index + 1,
            event_count: Number(milestone.event_count),
            streak_period: data.temp.streak_period,
            streak_unit: Number(milestone.streak_unit),
            outcomes: milestone.outcomes
          })) || [];
      }
    } else {
      if (data.streak_campaign_mechanics?.streak_type === 'cumulative_spend') {
        data.streak_intervals = [
          {
            id: data.temp?.interval_id,
            position: 1,
            target_spend: Number(data.temp.target_spend),
            streak_period: data.temp.streak_period,
            streak_unit: Number(data.temp.streak_unit),
            outcomes: data.outcomes || []
          }
        ];
      } else {
        data.streak_intervals = [
          {
            id: data.temp?.interval_id,
            position: 1,
            event_count: Number(data.temp.event_count),
            streak_period: data.temp.streak_period,
            streak_unit: Number(data.temp.streak_unit),
            outcomes: data.outcomes || []
          }
        ];
      }
    }
    if (data.id) {
      data.rules = data?.rules?.map(rule => {
        return {
          ...rule,
          event_params: {
            ...rule.event_params,
            campaign_id: Number(data.id)
          }
        };
      });
    }

    data.outcomes = [];
    data.milestones = [];
    delete data.temp;
  }

  if (data.campaign_type === 'instant_outcome') {
    const rules = data.rules || [];
    const ruleNameToFind = 'Push Notification Rule for Instant Outcome Transaction Issue';

    const existingRule = rules.find(rule => rule?.name === ruleNameToFind) || {};

    const remainingRules = rules.filter(rule => rule?.name !== ruleNameToFind);

    if (data.pushNotificationRule?.send_notification) {
      const newRule = {
        name: ruleNameToFind,
        rule_type: 'campaign_rule',
        event_name: 'campaign_completed',
        event_params: { campaign_id: Number(data.id) },
        ...existingRule,
        id: data.duplicateID ? null : data.pushNotificationRule?.ruleId,
        send_notification: data.pushNotificationRule?.send_notification || false,
        outcome_options: {
          selected_media_type_id:
            data.pushNotificationRule?.outcome_options?.selected_media_type_id || null,
          custom_fields: data.pushNotificationRule?.outcome_options?.custom_fields || {}
        }
      };
      delete newRule['send_notification_on_auto_redeem'];
      if (data?.pushNotificationRule?.ruleId)
        newRule.id = data.duplicateID ? null : data.pushNotificationRule.ruleId;
      remainingRules.push(newRule);
    }

    delete data.pushNotificationRule;
    data.rules = remainingRules;
  }

  // Choose Your Reward changes where Win Odds live for GAME campaigns only.
  //   - non-bubble_pop game CYR: no_outcome.probability holds the no-prize weight and
  //     winners split the remainder (100 - no_outcome.probability). The BE draw reads
  //     per-outcome probability (randomize_outcomes_service.rb:32), so keep it and
  //     re-apply the mapping at submit. Stripping it was the cause of Win Odds
  //     resetting to 100 on reopen. — PLA-8560
  //   - bubble_pop CYR: score-based, handled by transformBubblePopData; keep the strip.
  // Every other campaign type keeps the pre-existing behaviour untouched.
  const isGameCyr =
    data.campaign_type === 'game' &&
    data.game_type !== 'bubble_pop' &&
    !!data.outcome_choices_config?.enabled;

  if (data.outcome_choices_config?.enabled && !isGameCyr) {
    data.outcomes = data.outcomes.map(outcome => {
      const { probability, ...rest } = outcome;
      return rest;
    });
  } else if (isGameCyr && Array.isArray(data.outcomes)) {
    const winProbability = deriveWinProbability(data.outcomes);
    if (winProbability != null) {
      data.outcomes = applyWinProbability(data.outcomes, Number(winProbability));
    } else {
      // CYR on but "Include no prize as outcome" unchecked: no Win Odds row, so drop
      // any stale probability seeded before CYR was turned on.
      data.outcomes = data.outcomes.map(outcome => {
        const { probability, ...rest } = outcome;
        return rest;
      });
    }
  }

  if (data.game_type === 'bubble_pop') {
    data = transformBubblePopData(data);
  }

  if (!data['include_audience_file']) {
    delete data['include_audience_file'];
  }
  if (!data['exclude_audience_file']) {
    delete data['exclude_audience_file'];
  }

  if (data.rules?.length) {
    data.rules = data.rules.map(item => {
      const normalizedRule = {
        ...item,
        begins_at: data.start_date ?? item.begins_at ?? null,
        ends_at: data.end_date ?? null
      };

      if (normalizedRule?.labels?.length > 0) {
        normalizedRule.labels = normalizedRule.labels.map(label => label?.id);
      }
      if (normalizedRule.conditions === undefined) {
        normalizedRule.conditions = [];
      }

      return normalizedRule;
    });
  }

  if (data.game_type === 'quiz') {
    // TODO: remove. temp set question length as outcome position for BE
    //
    // PROD-1741: position must always track the CURRENT question count. This used to
    // return any pre-existing position untouched, so adding questions to an already
    // saved quiz left the threshold behind (a 3-question quiz grown to 5 kept
    // position 3). The BE closest-below ladder (Game::Quiz#calculate_position) then
    // issued the outcome to anyone scoring at or above the stale position, i.e. 3 of 5
    // correct still won.
    //
    // The old guard was `outcome.position && outcome.position > -1`, which never
    // preserved the VS-4684 position = 0 case anyway — 0 is falsy in JS, so it was
    // already overwritten on every save. Behaviour for 0 is therefore unchanged.
    data.outcomes = data.outcomes.map(outcome => ({
      ...outcome,
      position: data.display_properties.questions.length
    }));
  }

  if (data.game_type === 'quiz' || data.game_type === 'survey') {
    // remap display_properties to display_properties_en
    data.display_properties_en = { ...data.display_properties };
    delete data.display_properties;
  }

  if (data.campaign_type === 'raffle') {
    // TODO: currently backend throws an error on request if there are any extra parameters sent.
    // Therefore, strip out frontend-only variables.
    // In the future, it probably can be removed.
    data?.raffle_grand_prizes?.forEach(prize => {
      delete prize._id;

      prize?.outcomes?.forEach(outcome => {
        delete outcome._id;
      });
    });

    const raffleGrandPrizesCount = getRaffleGrandPrizePositionsCount(
      data.raffle_grand_prizes || []
    );
    const shouldRemovePrizeLimitToggleValue =
      typeof data.prizes_per_raffle_winner !== 'number' ||
      data.prizes_per_raffle_winner <= 0 ||
      (data.prizes_per_raffle_winner === 1 && raffleGrandPrizesCount < 2);

    if (shouldRemovePrizeLimitToggleValue) {
      delete data.prizes_per_raffle_winner;
    }
  }

  if (data.campaign_type !== 'raffle' && _has(data, 'prizes_per_raffle_winner')) {
    delete data.prizes_per_raffle_winner;
  }

  if (data.filters) {
    data.filters = _pickBy(data.filters, doesFilterHaveValue);
  }

  if (Array.isArray(data.outcomes) && data.outcomes.length !== 0) {
    const updatedOutcomes = data.outcomes.map(option => {
      if (option?.campaign_options?.outcome_properties) {
        return {
          ...option,
          campaign_options: {
            ...option.campaign_options,
            outcome_properties: _pickBy(
              option.campaign_options.outcome_properties,
              (_value, key) => key === 'gl_account_id'
            )
          }
        };
      }
      return option;
    });

    data.outcomes = updatedOutcomes;
  }

  // Extract rule_details from variable points outcomes and add to rules array.
  // The backend processes outcomes (campaign_modules) and rules (transaction_rules)
  // separately — variable points config must be sent in the rules array.
  // Scan top-level outcomes and nested outcomes (prize sets, randomized sets, etc.)
  if (Array.isArray(data.outcomes)) {
    const extractRuleDetails = (outcome: any) => {
      if (outcome.rule_details) {
        if (!Array.isArray(data.rules)) {
          data.rules = [];
        }
        const newRule = {
          ...outcome.rule_details,
          event_name: outcome.rule_details.event_name || 'transactions_created'
        };
        const existingIndex = data.rules.findIndex(
          r =>
            r.event_name === newRule.event_name &&
            r.rule_type === 'campaign_rule' &&
            !r.send_notification
        );
        if (existingIndex >= 0) {
          data.rules[existingIndex] = { ...data.rules[existingIndex], ...newRule };
        } else {
          data.rules.push(newRule);
        }
        delete outcome.rule_details;
      }
      // Check nested outcomes (prize sets, randomized outcome sets)
      if (Array.isArray(outcome.outcomes)) {
        outcome.outcomes.forEach(extractRuleDetails);
      }
    };
    data.outcomes.forEach(extractRuleDetails);
  }

  if (data.temp) {
    delete data.temp;
  }

  return data;
};

const updateCodeGenerationMethod = (data: any) => {
  data.code_generation_method = 'multiple_with_user_upload';
};

const handleCampaignDurationMechanics = (data: any) => {
  if (!data.campaign_duration_mechanics && 'campaign_duration_days' in data) {
    delete data.campaign_duration_days;
  }
  delete data.campaign_duration_mechanics;
};

const sanitizeCampaign = (clonedData: any, isImageFeatureEnabled = false) => {
  const data = _cloneDeep(clonedData);
  // remove display_properties from games and only use locale version
  const shouldSanitizeDisplayProperties = isImageFeatureEnabled
    ? data.campaign_type === 'game' && data.game_type !== 'quiz' && data.game_type !== 'survey'
    : (data.campaign_type === 'game' && data.game_type !== 'quiz' && data.game_type !== 'survey') ||
      data.campaign_type === 'stamp' ||
      data.campaign_type === 'quest' ||
      data.campaign_type === 'raffle';

  if (shouldSanitizeDisplayProperties) {
    sanitizeDisplayProperties(data);
  }

  // Check if code generation method needs to be updated
  const shouldUpdateCodeGenerationMethod =
    data && data.code_generation_method === 'multiple' && !data.user_generated;
  if (shouldUpdateCodeGenerationMethod) {
    updateCodeGenerationMethod(data);
  }

  const shouldHandleCampaignDurationMechanics =
    (data.campaign_type === 'quest' || data.campaign_type === 'progress') &&
    'campaign_duration_mechanics' in data;

  if (shouldHandleCampaignDurationMechanics) {
    handleCampaignDurationMechanics(data);
  }
  return data;
};

const sanitizeDisplayProperties = (data: any) => {
  const dataKeys = Object.keys(data);
  const displayPropertiesLocaleKeys = dataKeys.filter(k => k.startsWith('display_properties_'));

  // Clean image_id from display_properties before processing
  if (data.display_properties) {
    data.display_properties = cleanImageIds(data.display_properties);
  }

  // Get static display properties (non-textual, graphical data)
  const displayProperties = getStatic(data.campaign_type, data.game_type, data.display_properties);

  displayPropertiesLocaleKeys.forEach(dpLocale => {
    if (data[dpLocale] && Object.keys(data[dpLocale]).length > 0) {
      // Clean image_id from locale-specific display properties
      data[dpLocale] = cleanImageIds(data[dpLocale]);

      if (data.game_type === 'spin_the_wheel') {
        data[dpLocale].wedges = [];
      }
      // Merge using `customMergeForImages`
      data[dpLocale] = _mergeWith(
        {},
        data[dpLocale],
        displayProperties,
        (objValue, srcValue, key) => {
          if (key === 'wedges' && Array.isArray(srcValue)) {
            const array = Array.isArray(objValue) ? [...objValue, ...srcValue] : [...srcValue];
            const strigifyArray = array.map(val => JSON.stringify(val));
            return Array.from(new Set(strigifyArray)).map(val => JSON.parse(val));
          }
          return customMergeForImages(objValue, srcValue) ?? undefined;
        }
      );
    }
  });

  delete data.display_properties;
};
